import {CustomModal} from '_metronic/partials/modals/CustomModal'
import {useAuth} from 'app/modules/auth'
import {FC, useState} from 'react'
import Dropzone from 'react-dropzone'
import * as XLSX from 'xlsx'
import {addDoc, collection, doc, serverTimestamp, updateDoc} from 'firebase/firestore'
import db from 'services/firebase'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
}

interface ParsedData {
  [key: string]: string
}

const AddCSVModal: FC<Props> = ({isModalOpen, hideModal}) => {
  const {currentUser} = useAuth()

  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState<File[]>([])
  const [uploadedFile, setUploadedFile] = useState<{name: string; size: number} | null>(null)

  const handleUpload = (acceptedFiles: File[]) => {
    const xlsxFile = acceptedFiles.find((file) => file.name.toLowerCase().endsWith('.xlsx'))

    if (xlsxFile) {
      setFile([xlsxFile])
      setUploadedFile({
        name: xlsxFile.name,
        size: xlsxFile.size,
      })
    }
  }

  const fieldMapping = {
    'Control Title': 'title',
    'Control Description': 'description',
  }
  const handleRemoveFile = () => {
    setUploadedFile(null)
  }

  const currentDate = new Date() // No need to import the Firestore module
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1 // Note: The month is zero-based
  const day = currentDate.getDate()

  const todaysDate = month + '/' + day + '/' + year

  const okBtnClickHandler = async () => {
    setIsLoading(true)
    try {
      const xlsxFile = file[0]
      const fileReader = new FileReader()

      fileReader.onload = async (event) => {
        const arrayBuffer = event.target?.result as ArrayBuffer
        const workbook = XLSX.read(arrayBuffer, {type: 'array'})
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const data = XLSX.utils.sheet_to_json(worksheet, {header: 1, blankrows: false})

        if (data.length <= 1) {
          showErrorToast({message: 'No data found in the XLSX file'})
          setIsLoading(false)
          return
        }

        const headers = (data[0] as string[]).map((header) => header.trim())
        const expectedHeaders = Object.keys(fieldMapping).map((header) => header.trim())
        const isValid = expectedHeaders.every((header) => headers.includes(header))

        if (!isValid) {
          showErrorToast({message: 'Invalid headers in XLSX file'})
          setIsLoading(false)
          return
        }

        const validRows = data.slice(1).filter((row) => {
          const rowArray = row as (string | undefined | null)[]
          return (
            rowArray.length >= headers.length &&
            rowArray.some((cell) => cell && String(cell).trim() !== '')
          )
        })

        if (validRows.length === 0) {
          showErrorToast({message: 'No valid rows found in the XLSX file'})
          setIsLoading(false)
          return
        }

        const parsedDataArray = validRows.map((row, index) => {
          const parsedData: ParsedData = headers.reduce(
            (obj: ParsedData, header: string, colIndex: number) => {
              const targetField = fieldMapping[header]
              if (targetField) {
                const cellValue = (row as (string | undefined | null)[])[colIndex]
                obj[targetField] = cellValue ? String(cellValue).trim() : ''
              }
              return obj
            },
            {}
          )
          return parsedData
        })

        const filteredDataArray = parsedDataArray.filter((parsedData) => {
          const requiredFields = ['title', 'description']
          return requiredFields.every((field) => {
            const value = parsedData[field]
            return value !== undefined && value !== null && String(value).trim() !== ''
          })
        })

        if (filteredDataArray.length === 0) {
          showErrorToast({
            message: 'No valid data found after validation. Ensure all required fields are filled.',
          })
          setIsLoading(false)
          return
        }

        if (currentUser) {
          const tenantRef = doc(db, 'tenants', currentUser.tenantId!)
          const controlsRef = collection(tenantRef, 'trust_center', 'controls', 'all_controls')

          for (const parsedData of filteredDataArray) {
            const newDocRef = await addDoc(controlsRef, {
              ...parsedData,
              category: 'Custom',
              isCustom: true,
              isShownInTrustCenter: true,
            })

            await updateDoc(newDocRef, {
              id: newDocRef.id,
            })

            const activitiesRef = collection(tenantRef, 'activities')
            await addDoc(activitiesRef, {
              message: `${currentUser?.userName} uploaded a new custom control through csv '${xlsxFile?.name} for trust center'`,
              timestamp: serverTimestamp(),
              tenantId: currentUser?.tenantId,
              uid: currentUser?.uid,
              username: currentUser?.userName,
              date: todaysDate,
              email: currentUser?.email,
              target: 'Trust Center',
              action: 'Custom control added',
            })
          }
        }

        showSuccessToast({
          message: `${filteredDataArray.length} custom control(s) for trust center has been added successfully`,
        })

        setFile([])
        setUploadedFile(null)
        setIsLoading(false)
      }

      fileReader.onerror = (error) => {
        showErrorToast({message: 'Error reading XLSX file'})
        setIsLoading(false)
      }

      fileReader.readAsArrayBuffer(xlsxFile)
    } catch (err) {
      console.error('Error in handleSubmit:', err)
    }finally{
      setIsLoading(false)
      hideModal()
    }
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title='Import Controls'
      isCloseIconShown={true}
      okBtnText='Add Control'
      okBtnClickHandler={okBtnClickHandler}
      cancelBtnText={'Cancel'}
      isLoading={isLoading}
      isLoadingBtnText='Adding Controls...'
    >
      <div className='d-flex flex-column'>
        <div className='fs-6 fw-medium text-dark'>
          Please download this excel template for controls. Make sure you add all the required
          details and upload it here.
        </div>

        <div className='mt-4'>
          <div className={`card mb-4 mb-xl-8 mt-2`}>
            <div
              className='card-header border-0 pt-6 mb-5'
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{display: 'flex', alignItems: 'center'}}>
                <img
                  src='/media/icon/file2.svg'
                  alt=''
                  className='img-fluid'
                  style={{width: '50px', height: '50px', marginRight: '10px'}} // Adjust marginRight as needed
                />
                <div className='d-flex justify-content-start flex-column fw-bold fs-6'>
                  {/* {file[0].name} */}
                  Control Template.xlsx
                  <span className=' fw-semibold d-block fs-7' style={{color: '#5A5A5A'}}>
                    9 KB
                  </span>
                </div>
              </div>
              <div
                style={{
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = '#fff'
                    const svgElement = e.currentTarget.querySelector('svg')
                    if (svgElement) {
                      svgElement.style.stroke = '#0E9F6E'
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget) {
                    e.currentTarget.style.backgroundColor = '#fff'
                    const svgElement = e.currentTarget.querySelector('svg')
                    if (svgElement) {
                      svgElement.style.stroke = '#5A5A5A'
                    }
                  }
                }}
              >
                <a href='/templates/Control Template.xlsx' download>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='20'
                    height='20'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='#5A5A5A'
                    stroke-width='2'
                    stroke-linecap='round'
                    stroke-linejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-cloud-download mx-2 mb-1'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M19 18a3.5 3.5 0 0 0 0 -7h-1a5 4.5 0 0 0 -11 -2a4.6 4.4 0 0 0 -2.1 8.4' />
                    <path d='M12 13l0 9' />
                    <path d='M9 19l3 3l3 -3' />
                  </svg>
                  <span className='fw-semibold text-hover-primary' style={{color: '#5A5A5A'}}>
                    Download
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className='form'>
          <div className='form-group'>
            <div className='main-container'>
              <div className='fw-bold fs-6 mb-2 mt-4'> Your Uploads</div>
              {uploadedFile ? (
                <div className={`card mb-4 mb-xl-8 mt-4`}>
                  <div
                    className='card-header border-0 pt-6 mb-5'
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <img
                        src='/media/icon/file2.svg'
                        alt=''
                        className='img-fluid'
                        style={{width: '50px', height: '50px', marginRight: '10px'}}
                      />
                      <div className='d-flex justify-content-start flex-column fw-bold fs-6'>
                        {uploadedFile.name}
                        <span className='fw-semibold d-block fs-6' style={{color: '#5A5A5A'}}>
                          Size: {(uploadedFile.size / 1024).toFixed(2)} KB
                        </span>
                      </div>
                    </div>
                    <div>
                      {' '}
                      <span className='mx-1 fs-7'>Uploaded</span>{' '}
                      <span className='btn btn-icon btn-sm btn-active-light-primary'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='20'
                          height='20'
                          viewBox='0 0 24 24'
                          fill='#5A5A5A'
                          className='icon icon-tabler icons-tabler-filled icon-tabler-circle-x'
                          onClick={handleRemoveFile}
                        >
                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                          <path d='M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-6.489 5.8a1 1 0 0 0 -1.218 1.567l1.292 1.293l-1.292 1.293l-.083 .094a1 1 0 0 0 1.497 1.32l1.293 -1.292l1.293 1.292l.094 .083a1 1 0 0 0 1.32 -1.497l-1.292 -1.293l1.292 -1.293l.083 -.094a1 1 0 0 0 -1.497 -1.32l-1.293 1.292l-1.293 -1.292l-.094 -.083z' />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <Dropzone
                  onDrop={handleUpload}
                  minSize={0}
                  accept={{
                    'text/csv': [],
                    'application/vnd.ms-excel': [],
                    'application/csv': [],
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
                    '': ['.csv', '.xlsx', '.xls'],
                  }}
                  maxSize={10 * 1024 * 1024}
                >
                  {({getRootProps, getInputProps}) => (
                    <div
                      {...getRootProps({
                        className: 'dropzone',
                        style: {
                          backgroundColor: '#fff',
                          padding: '20px',
                          border: '2px dashed #ccc',
                          borderRadius: '4px',
                          textAlign: 'center',
                          cursor: 'pointer',
                        },
                      })}
                    >
                      <input {...getInputProps()} />
                      <img
                        src='/media/icon/uploadFile.svg'
                        alt=''
                        loading='lazy'
                        className='mb-4 mt-4'
                      />
                      <p className='fw-bold fs-6'>
                        Drag and drop or <span style={{color:'#0e9f6e'}}>select</span> a file
                      </p>
                      <p className='fs-6'>Upload upto 10 mb. Supported formats: xlsx</p>
                    </div>
                  )}
                </Dropzone>
              )}
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  )
}

export default AddCSVModal
